import * as actions from 'client/actions'
import * as pageActions from 'client/actions/pageActions'
import ConfirmationDialog from 'client/components/core/ConfirmationDialog'
import useManagement from 'client/hooks/useManagement'
import usePresentPage from 'client/hooks/usePresentPage'
import { useUnsavedData } from 'client/hooks/useUnsavedData'
import { selectors as managementSelectors } from 'client/reducers/managementReducer'
import { selectors as pageSelectors } from 'client/reducers/pageReducer'
import { usePage, useWebsiteSettings } from 'client/store'
import { BadRequest } from 'common/errors/BadRequest'
import { NetworkError } from 'common/errors/NetworkError'
import { NotFound } from 'common/errors/NotFound'
import { PageValidationError } from 'common/errors/PageValidationError'
import { useActions } from 'common/hooks/useActions'
import React, { useState } from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { useDispatch } from 'react-redux'
import { useWhiteLabelBrandSettings } from 'src/editor/hooks/use-white-label-brand'
import { PrimaryButton } from 'src/editor/shared/ui-lib/buttons'
import { twMerge } from 'tailwind-merge'
import { saveWebsitePage } from '../../api/website-pages-api'
import { useWebsitePageSavePayload } from '../../hooks/use-website-page-save-payload'
import { useWebsiteSave } from '../../hooks/use-website-save'
import { getWebsiteId } from '../../store/website-settings/website-settings-selector'

export const ExitPageButton = () => {
  const { t, i18n } = useTypedTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [isDialogOpen, openDialog] = useState(false)
  const dispatch = useDispatch()
  const isPageModified = usePresentPage(pageSelectors.isPageModified)
  const funnelPageExitUrl = useManagement(managementSelectors.getExitUrl)
  const isTemplate = usePage(pageSelectors.isTemplate)
  const savePage = useActions(pageActions.savePage)
  const exit = useActions(actions.exit)
  const websiteId = useWebsiteSettings(getWebsiteId)
  const exitUrl = websiteId ? '/dashboard/websites' : funnelPageExitUrl
  const { brandSettings } = useWhiteLabelBrandSettings()
  const pageId = usePage(pageSelectors.getPageId)
  // we need it only to delete record from db
  const { deleteDbRecord } = useUnsavedData({ notExecute: true })

  const pageSavePayload = useWebsitePageSavePayload()
  const saveWebsiteData = useWebsiteSave()

  async function handleSave() {
    setIsLoading(true)
    try {
      if (websiteId && pageSavePayload) {
        await saveWebsiteData(websiteId)
        await saveWebsitePage(pageSavePayload)
      } else {
        await savePage(isTemplate)
      }
      await deleteDbRecord()
      dispatch(pageActions.savePageSuccess())
    } catch (e) {
      if (e instanceof PageValidationError) {
        dispatch(pageActions.pageValidationFail(e.messages))
      } else if (e instanceof NotFound) {
        dispatch(pageActions.savePageFail('core.errors.page_not_found'))
      } else if (e instanceof NetworkError) {
        // just show warning about data loss
        dispatch(pageActions.savePageFail('core.errors.save'))
      } else if (e instanceof BadRequest) {
        dispatch(pageActions.savePageFail('core.errors.save'))
      } else {
        dispatch(pageActions.savePageFail('core.errors.save'))
      }
    }
    setIsLoading(false)
  }

  const handleExit = () => {
    exit(exitUrl)
  }

  const checkAndExit = () => {
    if (isPageModified) {
      openDialog(true)
    } else {
      exit(exitUrl)
    }
  }

  const saveAndExit = async () => {
    await handleSave()
    closeDialog()
    handleExit()
  }

  const closeDialog = () => openDialog(false)

  return (
    <React.Fragment>
      {isDialogOpen && (
        <ConfirmationDialog
          save={saveAndExit}
          close={closeDialog}
          exit={handleExit}
          isLoading={isLoading}
          title={'core.header.exit_confirmation_title'}
        />
      )}
      <PrimaryButton
        onClick={checkAndExit}
        title={t('core.header.exit')}
        disabled={!i18n.language || !pageId}
        data-test-id="editor-exit-button"
        className={twMerge(
          brandSettings?.colors &&
            'bg-brandSecondary text-brandTextPrimary hover:bg-brandSecondaryHover hover:text-brandTextPrimary active:bg-brandSecondaryPressed',
        )}
      >
        {t('core.header.exit')}
      </PrimaryButton>
    </React.Fragment>
  )
}
